import { useEffect, useRef, useState } from "react";

const NuestraHistoriaSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="max-w-4xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase mb-6">
          Cómo empezó todo
        </p>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-foreground leading-tight">
          De los Mac a la IA.
        </h2>
      </div>

      <div className="space-y-16">
        {/* El Origen */}
        <div 
          className={`transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex items-start gap-8">
            <div className="text-6xl md:text-7xl font-bold text-muted-foreground/20">
              01
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-light text-foreground mb-4">
                El origen
              </h3>
              <p className="text-lg md:text-xl font-light text-muted-foreground leading-relaxed mb-4">
                Todo empezó con los Mac en los departamentos gráficos. Mientras el mundo empresarial 
                usaba PCs, nosotros integrábamos ambos mundos: conectábamos Macs con la infraestructura 
                de Windows.
              </p>
              <p className="text-lg md:text-xl font-light text-foreground leading-relaxed">
                Vimos un problema: <strong>las PYMEs quedaban relegadas a un segundo plano</strong>. 
                Las grandes empresas tenían acceso a la mejor tecnología, pero los pequeños negocios 
                no tenían quién les acompañara. Decidimos ser nosotros.
              </p>
            </div>
          </div>
        </div>

        {/* La Evolución */}
        <div 
          className={`transition-all duration-700 ease-out delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex items-start gap-8">
            <div className="text-6xl md:text-7xl font-bold text-muted-foreground/20">
              02
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-light text-foreground mb-4">
                La evolución
              </h3>
              <p className="text-lg md:text-xl font-light text-muted-foreground leading-relaxed">
                De integrar Macs pasamos a construir infraestructuras completas. Redes, servidores, 
                comunicaciones. Cada vez que aparecía una nueva tecnología, la aprendíamos y la 
                llevábamos a nuestros clientes. Del módem de 128k al Cloud de AWS, Google y Azure.
              </p>
            </div>
          </div>
        </div>

        {/* Puntos de inflexión */}
        <div 
          className={`transition-all duration-700 ease-out delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex items-start gap-8">
            <div className="text-6xl md:text-7xl font-bold text-muted-foreground/20">
              03
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-light text-foreground mb-4">
                Puntos de inflexión
              </h3>
              <p className="text-lg md:text-xl font-light text-muted-foreground leading-relaxed mb-4">
                Un hotel nos pidió digitalizar toda su operación en la nube. Fue el momento en que 
                supimos que el Cloud no era el futuro — era el presente.
              </p>
              <p className="text-lg md:text-xl font-light text-muted-foreground leading-relaxed">
                Al incorporar Cloud, vimos la necesidad de proteger los activos. La ciberseguridad 
                se convirtió en una capa esencial de nuestro servicio.
              </p>
            </div>
          </div>
        </div>

        {/* La llegada de la IA */}
        <div 
          className={`transition-all duration-700 ease-out delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex items-start gap-8">
            <div className="text-6xl md:text-7xl font-bold text-muted-foreground/20">
              04
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-light text-foreground mb-4">
                La llegada de la IA
              </h3>
              <p className="text-lg md:text-xl font-light text-muted-foreground leading-relaxed mb-4">
                En 2023, la inteligencia artificial dejó de ser ciencia ficción. Vimos la misma oportunidad 
                que vimos con los Mac, con el Cloud, con la ciberseguridad: llevar tecnología transformadora 
                a quienes más la necesitan.
              </p>
              <p className="text-lg md:text-xl font-light text-foreground leading-relaxed">
                Hoy ayudamos a PYMEs a <strong>automatizar tareas repetitivas, analizar datos sin analistas, 
                atender clientes 24/7 y crear contenido profesional</strong>. Probamos cada herramienta antes 
                de recomendarla. No vendemos humo.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NuestraHistoriaSection;
